import { gql } from '@apollo/client';
import * as MediaQueries from './MediaQueries';
import * as BillingQueries from './BillingQueries';

export const MUTATION_LOGIN = gql`
  mutation login($email: String!, $password: String!) {
    token: login(email: $email, password: $password)
  }
`;

export const SET_USER_TOKEN = gql`
  mutation setUserToken($token: String!, $isLoggedIn: Boolean!) {
    setUserToken(token: $token, isLoggedIn: $isLoggedIn) @client
  }
`;

export const IS_LOGGED_IN = gql`
  query IsUserLoggedIn {
    token @client
    isLoggedIn @client
  }
`;

export const MUTATION_CREATE_USER = gql`
  mutation createAccount(
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String!
    $countryId: ID!
  ) {
    token: createAccount(
      input: { firstName: $firstName, lastName: $lastName, email: $email, password: $password, countryId: $countryId }
    )
  }
`;

export const ACCOUNT_COUNTRIES_FETCH = gql`
  query countries {
    countries {
      name
      id
      code
      enabled
      sortOrder
    }
  }
`;

export const PROFILE_CREATE = gql`
  mutation createProfile($input: ProfileCreateInput!) {
    createProfile(input: $input) {
      message
    }
  }
`;

export const MUTATION_CREATE_PROFILE = gql`
  mutation createProfile($stageName: String!, $minor: Boolean!, $accountId: Float, $profileStatusId: Int) {
    createProfile(
      input: { stageName: $stageName, minor: $minor, accountId: $accountId, profileStatusId: $profileStatusId }
    ) {
      profileId
      profileStatus {
        id
        code
        name
      }
    }
  }
`;

export const MUTATION_UPDATE_PROFILE = gql`
  mutation updateProfile($input: ProfileUpdateInput!) {
    updateProfile(input: $input) {
      profileId
      profileStatus {
        id
        code
        name
      }
    }
  }
`;

export const PROFILE_CREATE_FETCH = gql`
  query profileCreateFetch {
    dmas {
      id
      name
      code
      region {
        id
        name
        code
        country {
          id
          code
          name
          continent {
            id
          }
        }
      }
    }
    countries {
      id
      code
      name
      continent {
        id
      }
    }
    heights {
      id
      METRIC_CENTIMETERS
      IMPERIAL_INCHES
    }
    weights {
      id
      IMPERIAL_LBS
      METRIC_KG
    }
    seekingRepresentations {
      id
      name
    }
    hasRepresentation
    genderAppearances {
      id
      name
      code
    }
    genderIdentities {
      id
      name
      code
    }
    ethnicAppearances {
      id
      name
      code
      sortOrder
    }
    willingnesses {
      id
      code
      name
      parentObject {
        id
        name
        code
      }
      childObjects {
        id
        name
        code
      }
    }
    pursuings {
      code
      name
      id
    }
    talentUnions {
      id
      name
      code
      sortOrder
      parentObject {
        id
        code
        name
        sortOrder
      }
    }
  }
`;

export const GET_PROFILE_TYPE = gql`
  {
    isAdultProfile @client
  }
`;

export const PROFILE_DETAIL = gql`
  query PROFILE_DETAIL($ids: ID!) {
    profiles(ids: [$ids]) {
      profileId
      stageName
      blurb
      otherExperiences
      hideCreditYear
      artistId
      talentEmail
      managerEmail
      phone
      isOverscaled
      overscaleNote
      defaultSubmissionNote
      notes {
        id
        comment
        created
        noteModifier {
          id
          code
        }
      }
      conflicts {
        profileConflictId
        conflictId
        expiration
        note
      }
      profileBookOuts {
        profileBookoutId
        startDate
        endDate
      }
      multipleBirth {
        id
        name
      }
      mediaAssets {
        ...ProfileMediaWithThumbnail
      }
      billingItems {
        ...BillingFeatures
      }
      height {
        id
        IMPERIAL_FEET_INCHES
        METRIC_CENTIMETERS
      }
      weight {
        id
        IMPERIAL_LBS
        METRIC_KG
        kgRounded
      }
      waist {
        id
        inch
        cm
        inchRounded
      }
      inseam {
        id
        inch
        cm
        inchRounded
      }
      gloveSize {
        id
        inch
        cm
        inchRounded
      }
      hatSize {
        id
        inch
        cm
      }
      profileTshirtSizes {
        id
        name
        category {
          id
          name
        }
      }
      profileShoeSizes {
        id
        shoeSizeUS
        shoeSizeUK
        shoeSizeEU
        shoeSizeAU
        category {
          id
          name
        }
        shoeModifier {
          id
          name
        }
      }
      dressSize {
        id
        sizeUS
        sizeUK
        sizeEU
        sizeAU
      }
      bust {
        id
        inch
        cm
        inchRounded
      }
      underbust {
        id
        inch
        cm
        inchRounded
      }
      hipSize {
        id
        inch
        cm
        inchRounded
      }
      cupSize {
        id
        name
      }
      profilePants {
        id
        sizeUS
        sizeUK
        sizeEU
        sizeAU
        gender {
          id
          code
          name
        }
      }
      chestSize {
        id
        inch
        cm
        inchRounded
      }
      sleeve {
        id
        inch
        cm
        inchRounded
      }
      neck {
        id
        inch
        cm
        inchRounded
      }
      coat {
        id
        name
      }
      profileSizes {
        id
        name
        category {
          id
          name
        }
        sizeModifier {
          id
          name
        }
      }
      playableAgeMin
      playableAgeMax
      playableAge
      profileGenderIdentities {
        genderIdentity {
          id
          code
          name
        }
        genderOtherIdentityNote
      }
      genderAppearances {
        id
        code
        name
      }
      ethnicAppearances {
        id
        name
        order
      }
      hairColor {
        id
        name
      }
      willingness {
        id
        code
        name
        childObjects {
          id
          name
          code
        }
      }
      hairStyle {
        id
        name
      }
      facialHair {
        id
        name
      }
      eyeColor {
        id
        name
      }
      tattoo {
        id
        name
      }
      tattooNote
      profilePiercings {
        id
        name
      }
      piercingNote
      pursuing {
        id
        name
      }
      uniqueAttributes {
        id
        name
      }
      workingLocations {
        id
        name
        sortOrder
      }
      seekingRepresentations {
        id
        name
      }
      hasRepresentation
      profession
      pregnancy
      profileMedia {
        media {
          mediaId
          name
          guid
        }
      }
      talentUnions {
        id
        name
        sortOrder
      }
      skills {
        id
        code
        name
        profileMediaId
        parentObject {
          id
          name
          code
          parentObject {
            id
            name
          }
        }
        level {
          id
          name
        }
      }
      profileVehicles {
        vehicleName
        vehicleYear
        vehicleTypeMake {
          make {
            id
            name
          }
        }
      }
      profilePets {
        id
        name
      }
      props {
        id
        name
      }
      sportEquipments {
        id
        name
      }
      profileWardrobes {
        id
        name
      }
      documentTypes {
        id
        name
      }
      resume {
        media {
          mediaId
          name
          guid
          uploadPath
        }
      }
      dateOfBirth
    }
  }
  ${MediaQueries.profileMediaFragment}
  ${BillingQueries.billingItemsFragment}
`;

export const ADD_PHOTO_MUTATION = gql`
  mutation addPhoto($mediaInput: MediaInput!, $accountId: Float) {
    addPhoto(input: $mediaInput, accountId: $accountId) {
      mediaId
      name
      tag
      uploadPath
      fileType {
        id
        code
        name
      }
      mediaType {
        id
        code
        name
      }
      mediaStatus {
        id
        code
        name
      }
    }
  }
`;

export const ADD_VIDEO_MUTATION = gql`
  mutation addVideo($mediaInput: MediaInput!, $accountId: Float) {
    addVideo(input: $mediaInput, accountId: $accountId) {
      mediaId
      name
      tag
      uploadPath
      fileType {
        id
        code
        name
      }
      mediaType {
        id
        code
        name
      }
      mediaStatus {
        id
        code
        name
      }
    }
  }
`;

export const ADD_AUDIO_MUTATION = gql`
  mutation addAudio($mediaInput: MediaInput!, $accountId: Float) {
    addAudio(input: $mediaInput, accountId: $accountId) {
      mediaId
      name
      tag
      uploadPath
      fileType {
        id
        code
        name
      }
      mediaType {
        id
        code
        name
      }
      mediaStatus {
        id
        code
        name
      }
    }
  }
`;

export const UPDATE_PROFILE_MEDIA_MUTATION = gql`
  mutation updateProfileMedia($profileMedia: ProfileMediaInput!) {
    updateProfileMedia(profileMedia: $profileMedia)
  }
`;

export const UPDATE_PROFILE_MEDIA_LIST_MUTATION = gql`
  mutation updateProfileMediaList($profileMediaList: [ProfileMediaInput!]!) {
    updateProfileMediaList(profileMediaList: $profileMediaList)
  }
`;

export const REMOVE_PROFILE_RESUME_MUTATION = gql`
  mutation removeProfileResume($profileId: Int!) {
    removeProfileResume(profileId: $profileId)
  }
`;

export const SET_PROFILE_RESUME_MUTATION = gql`
  mutation setProfileResume($fileInput: FileInput!, $profileId: Float!) {
    setProfileResume(fileInput: $fileInput, profileId: $profileId) {
      mediaId
    }
  }
`;

export const GET_ACCOUNT_DETAILS_INFO = gql`
  query getComplexAccountInfo($ids: Float!) {
    getComplexAccountInfo(accountId: $ids) {
      firstName
      id
      lastName
      countryId
      country {
        id
        code
      }
      artists {
        minor
        artistId
        profiles {
          profileId
          stageName
          dateOfBirth
          profileStatus {
            id
            code
            name
          }
        }
      }
    }
  }
`;

export const GET_UPDATED_PROFILE_FIELD = gql`
  {
    updatedProfileField @client
  }
`;

export const SET_UPDATED_PROFILE_FIELD = gql`
  mutation setUpdatedProfileField($updatedProfileField: String!) {
    setUpdatedProfileField(updatedProfileField: $updatedProfileField) @client
  }
`;

export const GET_ALL_CONFLICTS = gql`
  query {
    conflicts {
      id
      code
      name
      selectable
      parentObject {
        id
        name
      }
      childObjects {
        id
        name
      }
    }
  }
`;

export const GET_SELECTED_DIVISION = gql`
  query organizations($id: ID!) {
    organizations(ids: [$id]) {
      id
      name
      parentOrganization {
        id
        name
      }
    }
  }
`;

export const PROFILE_STATUSES = gql`
  query profileStatuses {
    profileStatuses {
      id
      code
      name
    }
  }
`;

export const SOCIAL_MEDIA_FRAGMENT = gql`
  fragment ProfileSocialMedia on SocialMedia {
    __typename
    id
    instagram {
      id
      __typename
      isTokenValid
      link
      username
      metrics {
        __typename
        followerCount
      }
    }
    youtube {
      id
      __typename
      isTokenValid
      link
      username
      metrics {
        __typename
        followerCount
      }
    }
    tiktok {
      id
      __typename
      isTokenValid
      link
      username
      metrics {
        __typename
        followerCount
      }
    }
  }
`;
